/**
 * Books Health Check modal — runs the deterministic reconciliation rules and
 * lists anything that doesn't tie out, grouped by severity. Read-only; it points
 * you to the fix (e.g. the rip cost-basis repair) rather than changing data.
 */
import { runBooksHealthCheck, booksHealthSummary, type HealthIssue, type Severity } from "../lib/booksHealth";
import { icon as svgIcon } from "../utils/icon";
import { openModal } from "./modal";
import { escapeHtml } from "../lib/dom";

const META: Record<Severity, { icon: string; cls: string; label: string }> = {
  error: { icon: svgIcon("alert-circle"),   cls: "text-rose-300",  label: "Error" },
  warn:  { icon: svgIcon("alert-triangle"), cls: "text-amber-300", label: "Warning" },
  info:  { icon: svgIcon("info"),           cls: "text-cyan-300",  label: "Info" },
};

function issueRow(i: HealthIssue): string {
  const m = META[i.severity];
  return `<div class="flex items-start gap-2 py-1.5 border-b border-[var(--border)]">
    <span class="${m.cls} shrink-0" title="${m.label}">${m.icon}</span>
    <div class="min-w-0">
      <span class="text-xs uppercase tracking-wider text-[var(--muted)]">${escapeHtml(i.category)}</span>
      <div class="text-sm">${escapeHtml(i.message)}</div>
    </div>
  </div>`;
}

export function openBooksHealth(): void {
  const issues = runBooksHealthCheck();
  const s = booksHealthSummary(issues);

  let body: string;
  if (!issues.length) {
    body = `<div class="text-center py-8">
      <div class="mb-2 text-emerald-300 inline-flex">${svgIcon("check-circle", { size: 48 })}</div>
      <div class="font-semibold text-emerald-300">Everything reconciles.</div>
      <div class="text-sm text-[var(--muted)] mt-1">No issues found across inventory, rips, sales, trades, purchases, cash, and owner balances.</div>
    </div>`;
  } else {
    const chip = (n: number, cls: string, label: string) =>
      `<span class="px-2 py-0.5 rounded ${cls}" style="background:var(--panel-2)"><b>${n}</b> ${label}${n === 1 ? "" : "s"}</span>`;
    body = `
      <div class="flex flex-wrap gap-2 text-xs mb-3">
        ${s.errors ? chip(s.errors, "text-rose-300", "error") : ""}
        ${s.warns ? chip(s.warns, "text-amber-300", "warning") : ""}
        ${s.infos ? chip(s.infos, "text-cyan-300", "note") : ""}
      </div>
      <div class="panel scrollx" style="max-height:55vh; overflow:auto; padding:0 .75rem">
        ${issues.map(issueRow).join("")}
      </div>
      <div class="text-xs text-[var(--muted)] mt-3">These checks are deterministic — same data, same result. Errors mean numbers are off and should be fixed before trusting reports; warnings and notes are worth a look. Re-open this anytime after making changes.</div>`;
  }

  const footer = `<button class="btn btn-ghost" onclick="closeModal()">Close</button>`;
  openModal({ title: "Books Health Check", body, footer, wide: true });
}

(window as any).openBooksHealth = openBooksHealth;
